import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  categoryId: string | null;
}

export function GenerateInvoicesDialog({ open, onOpenChange, categoryId }: Props) {
  const qc = useQueryClient();
  const [installmentId, setInstallmentId] = useState<string>("");
  const [generating, setGenerating] = useState(false);

  const { data: category } = useQuery({
    queryKey: ["fee_category_full", categoryId],
    enabled: !!categoryId && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fee_categories")
        .select("*, fee_installments(*)")
        .eq("id", categoryId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => { if (open) setInstallmentId(""); }, [open]);

  const handleGenerate = async () => {
    if (!category || !installmentId) return;
    const inst = category.fee_installments.find((i: any) => i.id === installmentId);
    if (!inst) return;

    setGenerating(true);
    try {
      const { data: students, error: sErr } = await supabase
        .from("students")
        .select("id, first_name, last_name")
        .in("class", category.classes);
      if (sErr) throw sErr;
      if (!students?.length) {
        toast({ title: "No students", description: `No students found for classes: ${category.classes.join(", ")}`, variant: "destructive" });
        setGenerating(false);
        return;
      }

      const invoices = students.map(s => ({
        student_id: s.id,
        amount: inst.amount,
        due_date: inst.due_date,
        description: `${category.name} — ${inst.label}`,
        status: "unpaid",
      }));
      const { error: invErr } = await supabase.from("invoices").insert(invoices);
      if (invErr) throw invErr;

      toast({ title: "Invoices generated", description: `${invoices.length} invoices created for ${inst.label}.` });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Invoices from Structure</DialogTitle>
          <DialogDescription>{category ? <>For <strong>{category.name}</strong> ({category.classes.join(", ")})</> : "Loading…"}</DialogDescription>
        </DialogHeader>

        {category && (
          <div className="space-y-3">
            <div>
              <Label>Select Installment</Label>
              <Select value={installmentId} onValueChange={setInstallmentId}>
                <SelectTrigger><SelectValue placeholder="Pick an installment" /></SelectTrigger>
                <SelectContent>
                  {category.fee_installments.sort((a: any, b: any) => (a.semester + a.installment_no).localeCompare(b.semester + b.installment_no)).map((i: any) => (
                    <SelectItem key={i.id} value={i.id}>Sem {i.semester} #{i.installment_no} — {i.label} — TZS {Number(i.amount).toLocaleString()} (due {i.due_date})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">An invoice will be created for every student currently in classes: {category.classes.join(", ")}.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={!installmentId || generating}>{generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Generate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
